import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { useSubscriptionLimits } from "@/hooks/useSubscriptionLimits";
import { Crown, Calendar, Users, DollarSign, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";

interface StripePrices {
  essencial?: {
    id: string;
    unit_amount: number;
    currency: string;
  };
  profissional?: {
    id: string;
    unit_amount: number;
    currency: string;
  };
  corporativo?: {
    id: string;
    unit_amount: number;
    currency: string;
  };
}

export default function ConfiguracaoPlanos() {
  const { userOrgInfo } = useOrgPermissions();
  const { 
    canCreate, 
    currentCount, 
    limitCount, 
    planName, 
    expiresAt, 
    loading 
  } = useSubscriptionLimits();
  
  const [stripePrices, setStripePrices] = useState<StripePrices>({});
  const [loadingPrices, setLoadingPrices] = useState(true);

  useEffect(() => {
    const fetchStripePrices = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('stripe-prices');
        
        if (error) {
          console.error('Erro ao buscar preços:', error);
          return;
        }
        
        setStripePrices(data.prices || {});
      } catch (error) {
        console.error('Erro ao buscar preços do Stripe:', error);
      } finally {
        setLoadingPrices(false);
      }
    };

    fetchStripePrices();
  }, []);

  const formatPlanName = (plan: string) => {
    const planNames = {
      'essencial': 'Plano Essencial',
      'profissional': 'Plano Profissional',
      'corporativo': 'Plano Corporativo'
    };
    return planNames[plan as keyof typeof planNames] || plan;
  };

  const getPlanPrice = (plan: string) => {
    const priceInfo = stripePrices[plan as keyof StripePrices];
    if (priceInfo && priceInfo.unit_amount) {
      const value = priceInfo.unit_amount / 100; // Stripe retorna centavos
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    }
    return 'Carregando...';
  };

  const getPlanColor = (plan: string) => {
    const colors = {
      'essencial': 'bg-blue-500',
      'profissional': 'bg-purple-500',
      'corporativo': 'bg-yellow-500'
    };
    return colors[plan as keyof typeof colors] || 'bg-gray-500';
  };

  const usagePercentage = limitCount > 0 ? (currentCount / limitCount) * 100 : 0;
  const remainingCount = limitCount - currentCount;

  if (loading || loadingPrices) {
    return (
      <div className="container mx-auto py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center space-x-2">
        <Crown className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">Configurações do Plano</h1>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Informações do Plano Atual */}
        <Card>
          <CardHeader className="flex flex-row items-center space-y-0 pb-2">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${getPlanColor(planName)}`}></div>
              <CardTitle className="text-lg">{formatPlanName(planName)}</CardTitle>
            </div>
            <Badge variant="secondary" className="ml-auto">
              Ativo
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Valor mensal:</span>
              <span className="font-semibold">{getPlanPrice(planName)}</span>
            </div>
            
            {expiresAt && (
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Válido até:</span>
                <span className="font-semibold">
                  {format(new Date(expiresAt), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </span>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Organização:</span>
              <span className="font-semibold">{userOrgInfo.orgName}</span>
            </div>
          </CardContent>
        </Card>

        {/* Uso de Contratações */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Uso de Contratações</CardTitle>
            <CardDescription>
              Controle do limite mensal de contratações
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Usadas este mês</span>
              <span className="font-semibold text-lg">
                {currentCount} / {limitCount}
              </span>
            </div>
            
            <Progress 
              value={usagePercentage} 
              className="w-full"
            />
            
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Restam {remainingCount} contratações
              </span>
              <span className={`font-medium ${usagePercentage > 80 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {usagePercentage.toFixed(1)}%
              </span>
            </div>

            {!canCreate && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Limite de contratações atingido para este mês. Considere fazer upgrade do seu plano.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ações do Plano */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Gerenciar Plano</CardTitle>
          <CardDescription>
            Atualize ou modifique sua assinatura
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-4">
          <Button variant="outline">
            Alterar Plano
          </Button>
          <Button variant="outline">
            Histórico de Pagamentos
          </Button>
          <Button variant="outline">
            Suporte
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}